import { ZAttrMeta } from "src/business/attribute";
import { ZAttrComponent } from "src/business/attribute/attrEdits";
import { getAttrUserType } from "src/business/attribute/AttrUserType";

import { buildOrderRecords } from "./OptionsEditor/buildOrderRecords";
import { makeMetaFromOrderRecord } from "./OptionsEditor/makeMetaFromOrderRecord";
import { OptionRecord } from "./OptionsEditor/OptionRecord";

/**
 * Сначала хотел сопоставлять поля формы и ZAttrMeta
 * Но оказалось, что структуры отличаются слишком сильно.
 * Например, поля numberMin и numberMax в метаданных реализованы через правила.
 * Поэтому пришлось делать потенциально багоопасную трансформацию ☠️
 *
 * В случае появления новых метаданных нужно поддерживать следующие шаги:
 * 1. Внести новое поле в структуру AttrEditorData
 * 2. Добавить условие видимости в isFieldVisible
 * 3. Внести соответствующие изменения в трансформеры meta2edit и edit2meta
 * 4. Желательно добавить соответствующие юнит-тесты для трансформеров
 * 5. Добавить соответствующий JSX-код внутри формы в AttrEditorV2
 */

export type AttrEditorData = {
  name: string;
  title: string;
  required: boolean;

  order?: number;
  colSpan?: number;
  newLine?: boolean;

  numberPrecision?: number;
  numberMin?: number;
  numberMax?: number;
  numberDefaultValue?: number;

  textComponent?: ZAttrComponent["type"];
  textMaxLength?: number;
  textDefaultValue?: string;
  textPattern?: string;
  textPatternMsg?: string;

  dateOnly?: boolean;
  // Сейчас можно указать только абсолютное значение даты (см. ZDateDefault.ts)
  dateDefaultValueAbs?: string;

  boolDefaultValue?: BoolKey;

  maxCount?: number;
  maxSize?: number;
  fileAccept?: string;

  // выбор из каталога значений
  valuesCatalogComponent?: ZAttrComponent["type"];
  valuesCatalogId?: string;
  valueCatalogAttr?: string;

  linkToRecordId?: string;

  inlineOptions?: OptionRecord[];
};

const maxColSpan = 3;

export type AttrEditorField = keyof AttrEditorData;

export type BoolKey = "False" | "True" | "Undefined";
type BoolValue = {
  value: BoolKey;
  boolValue: boolean | undefined;
  label: string;
};
export const boolOptions: BoolValue[] = [
  { value: "True", boolValue: true, label: "True" },
  { value: "False", boolValue: false, label: "False" },
];

/* eslint consistent-return: "off" */

export const isFieldVisible = (
  field: AttrEditorField,
  attr: ZAttrMeta | undefined,
): boolean => {
  // eslint-disable-next-line default-case
  switch (field) {
    case "name":
    case "title":
    case "order":
      return true;
    case "required":
      return attr?.type.name !== "boolean";
    case "colSpan":
    case "newLine":
      return attr?.type.name !== "list";

    case "numberPrecision":
      return attr?.type.name === "number" && attr?.type.precision !== 0;
    case "numberMin":
    case "numberMax":
    case "numberDefaultValue":
      return attr?.type.name === "number";

    case "textComponent":
    case "textMaxLength":
    case "textDefaultValue":
    case "textPattern":
    case "textPatternMsg":
      return attr?.type.name === "string";

    case "dateOnly":
    case "dateDefaultValueAbs": // Сейчас поддерживается только абсолютная дата. Поэтому данное поле всегда видимо для типа dateTime
      return attr?.type.name === "dateTime";

    case "boolDefaultValue":
      return attr?.type.name === "boolean";
    case "maxCount":
      return attr?.type.name === "list" || attr?.type.name === "array";
    case "maxSize":
    case "fileAccept":
      return attr?.edit?.component?.type === "Uploader";

    case "valuesCatalogComponent":
    case "valuesCatalogId":
    case "valueCatalogAttr":
      return !!attr && getAttrUserType(attr)?.key === "valuesCatalog";

    case "linkToRecordId":
      return attr?.edit?.component?.type === "RecordSelect";

    case "inlineOptions":
      return (
        attr?.edit?.component?.type === "Select" &&
        attr.edit.component.options.type === "inline"
      );
  }
};

export const meta2edit = (srcAttr: ZAttrMeta): AttrEditorData => {
  const values: AttrEditorData = {
    name: srcAttr.name,
    title: srcAttr.title,
    required: srcAttr.required ?? false,
    order: srcAttr.edit?.order ?? undefined,
  };

  if (isFieldVisible("colSpan", srcAttr)) {
    const colSpan = srcAttr?.edit?.colSpan;
    values.colSpan = colSpan === "*" ? maxColSpan : (colSpan ?? 1);

    values.newLine = srcAttr?.edit?.newLine ?? false;
  }

  if (srcAttr.type.name === "string") {
    values.textComponent = srcAttr.edit?.component?.type;
    values.textMaxLength = srcAttr.type.maxLength;
    values.textDefaultValue = srcAttr.type.defaultValue ?? undefined;
    const rule = srcAttr.edit?.rules?.find(({ type }) => type === "pattern");
    if (rule?.type === "pattern") {
      values.textPattern = rule.pattern;
      values.textPatternMsg = rule.msg ?? undefined;
    }
  } else if (srcAttr.type.name === "number") {
    if (isFieldVisible("numberPrecision", srcAttr)) {
      // Согласно требованиям, это обязательное св-во с дефолтным значением 2
      // Хотя в метаданных оно не обязательное. И в случае отсутствия точность остаётся такой, как указал пользователь.
      values.numberPrecision = srcAttr.type.precision ?? 2;
    }
    srcAttr.edit?.rules?.forEach((rule) => {
      if (rule.type === "min") {
        values.numberMin = rule.minValue;
      } else if (rule.type === "max") {
        values.numberMax = rule.maxValue;
      }
    });
    values.numberDefaultValue = srcAttr.type.defaultValue ?? undefined;
  } else if (srcAttr.type.name === "dateTime") {
    values.dateOnly = !!srcAttr.type.dateOnly;
    const v = srcAttr.type.defaultValue;
    values.dateDefaultValueAbs = v?.type === "absolute" ? v.value : "";
  } else if (srcAttr.type.name === "boolean") {
    const { defaultValue } = srcAttr.type;
    values.boolDefaultValue =
      boolOptions.find(({ boolValue }) => defaultValue === boolValue)?.value ??
      "False";
  }
  if (srcAttr.type.name === "list" || srcAttr.type.name === "array") {
    values.maxCount = srcAttr.type.maxCount ?? undefined;
  }
  if (srcAttr.edit?.component?.type === "Uploader") {
    values.maxSize = srcAttr.edit?.component?.maxSize;
    values.fileAccept = srcAttr.edit?.component?.accept ?? undefined;
  }
  const userType = getAttrUserType(srcAttr);
  const comp = srcAttr.edit?.component;
  if (userType?.key === "valuesCatalog" && comp) {
    values.valuesCatalogComponent = comp.type;
    const cvtAttr = (
      template: string | null | undefined,
    ): string | undefined => {
      // Здесь действует ограничение потому что так захотели аналитики и заказчики.
      // Предполагается что шаблон всегда состоит только из одного атрибута.
      const res = /^{{([^{}]+)}}$/.exec(template ?? "");
      if (!res) return undefined;
      return res[1];
    };
    if (comp.type === "Select" && comp.options.type === "valuesCatalog") {
      values.valuesCatalogId = comp.options.valuesCatalogId;
      values.valueCatalogAttr = cvtAttr(comp.options.labelTemplate);
    } else if (comp.type === "SelectValuesModal") {
      values.valuesCatalogId = comp.valuesCatalogId;
      values.valueCatalogAttr = cvtAttr(comp.labelTemplate);
    }
  }
  if (comp?.type === "RecordSelect") {
    values.linkToRecordId = comp.catalogId;
  }

  values.inlineOptions = buildOrderRecords(srcAttr);

  return values;
};

/**
 * Редактированию подлежат не все свойства. (Н.р. не редактируется тип, uid, displayId)
 * Поэтому необходимо передавать исходные метаданные, чтобы получить новые.
 * @param srcAttr
 * @param ed Редактируемые данные
 * @returns Новый экземпляр метаданных (srcAttr не изменяется)
 */
export const edit2meta = (
  srcAttr: ZAttrMeta,
  ed: AttrEditorData,
): ZAttrMeta => {
  // Клонируется исходный атрибут, а модифицированные данные вносятся далее
  let dstAttr: ZAttrMeta = {
    ...JSON.parse(JSON.stringify(srcAttr)),
    name: ed.name,
    title: ed.title,
    required: ed.required,
  };
  dstAttr.edit = dstAttr.edit ?? {};
  dstAttr.edit.order = ed.order;

  if (isFieldVisible("colSpan", srcAttr)) {
    if (typeof ed.colSpan === "number") dstAttr.edit.colSpan = ed.colSpan;
    dstAttr.edit.newLine = ed.newLine;
  }

  if (dstAttr.type.name === "string") {
    dstAttr.type.maxLength = ed.textMaxLength ?? undefined;
    dstAttr.type.defaultValue = ed.textDefaultValue;
    const { textComponent, textPattern } = ed;
    if (textComponent === "Input" || textComponent === "TextArea") {
      if (dstAttr.edit.component?.type !== textComponent) {
        dstAttr.edit.component = { type: textComponent };
      }
    }
    if (textPattern) {
      const rule = dstAttr.edit.rules?.find(({ type }) => type === "pattern");
      if (rule?.type === "pattern") {
        rule.pattern = textPattern;
        rule.msg = ed.textPatternMsg;
      } else {
        dstAttr.edit.rules = dstAttr.edit.rules ?? [];
        dstAttr.edit.rules.push({
          type: "pattern",
          pattern: textPattern,
          msg: ed.textPatternMsg,
        });
      }
    } else {
      // Если нет шаблона, но в атрибуте есть правило, то его надо удалить
      const pos = dstAttr.edit?.rules?.findIndex(
        ({ type }) => type === "pattern",
      );
      if (typeof pos === "number" && pos >= 0) {
        dstAttr.edit.rules?.splice(pos, 1);
      }
    }
  }

  if (dstAttr.type.name === "number") {
    dstAttr.type.defaultValue = ed.numberDefaultValue;
    if (isFieldVisible("numberPrecision", srcAttr)) {
      dstAttr.type.precision = ed.numberPrecision;
    }

    if (typeof ed.numberMin === "number") {
      dstAttr.edit.rules = dstAttr.edit.rules ?? [];
      const rule = dstAttr.edit.rules.find((r) => r.type === "min");
      if (rule) {
        rule.minValue = ed.numberMin;
      } else {
        dstAttr.edit.rules.push({ type: "min", minValue: ed.numberMin });
      }
    } else if (dstAttr.edit?.rules) {
      dstAttr.edit.rules = dstAttr.edit.rules.filter(
        ({ type }) => type !== "min",
      );
    }

    if (typeof ed.numberMax === "number") {
      dstAttr.edit.rules = dstAttr.edit.rules ?? [];
      const rule = dstAttr.edit.rules.find((r) => r.type === "max");
      if (rule) {
        rule.maxValue = ed.numberMax;
      } else {
        dstAttr.edit.rules.push({ type: "max", maxValue: ed.numberMax });
      }
    } else if (dstAttr.edit?.rules) {
      dstAttr.edit.rules = dstAttr.edit.rules.filter(
        ({ type }) => type !== "max",
      );
    }
  }

  if (dstAttr.type.name === "dateTime") {
    dstAttr.type.dateOnly = !!ed.dateOnly;
    // если заполнено поле даты, то используется режим "absolute". Другие режимы сейчас не поддерживаются
    if (ed.dateDefaultValueAbs) {
      dstAttr.type.defaultValue = {
        type: "absolute",
        value: ed.dateDefaultValueAbs,
      };
    } else {
      dstAttr.type.defaultValue = undefined;
    }
  }
  if (dstAttr.type.name === "boolean") {
    dstAttr.type.defaultValue =
      boolOptions.find(({ value }) => value === ed.boolDefaultValue)
        ?.boolValue ?? false;
  }
  if (dstAttr.type.name === "list" || dstAttr.type.name === "array") {
    dstAttr.type.maxCount = ed.maxCount ?? undefined;
  }
  if (dstAttr.edit?.component?.type === "Uploader") {
    dstAttr.edit.component.maxSize = ed.maxSize ?? undefined;
    dstAttr.edit.component.accept = ed.fileAccept || undefined;
  }

  if (dstAttr.type.name === "strOption") {
    if (ed.valuesCatalogComponent === "Select") {
      dstAttr.edit.component = {
        type: "Select",
        options: {
          type: "valuesCatalog",
          valuesCatalogId: ed.valuesCatalogId ?? "",
          labelTemplate: `{{${ed.valueCatalogAttr}}}`,
        },
        search: "part", // Это пока хардкод
      };
    } else if (ed.valuesCatalogComponent === "SelectValuesModal") {
      dstAttr.edit.component = {
        type: "SelectValuesModal",
        valuesCatalogId: ed.valuesCatalogId ?? "",
        labelTemplate: `{{${ed.valueCatalogAttr}}}`,
      };
    }
  }
  if (dstAttr.edit?.component?.type === "RecordSelect") {
    dstAttr.edit.component.catalogId = ed.linkToRecordId ?? "";
  }
  if (isFieldVisible("inlineOptions", dstAttr)) {
    dstAttr = makeMetaFromOrderRecord(dstAttr, ed.inlineOptions);
  }

  return dstAttr;
};
